import { useActiveProfile } from '@app/auth/active-profile-context';
import { useState, useEffect } from 'react';
import { useAuth } from '@common/auth/use-auth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@common/http/query-client';
import { Outlet, useLocation } from 'react-router-dom';
import { AddIcon } from '@ui/icons/material/Add';
import { EditIcon } from '@ui/icons/material/Edit';
import { DeleteIcon } from '@ui/icons/material/Delete';
import { CheckIcon } from '@ui/icons/material/Check';
import { CloseIcon } from '@ui/icons/material/Close';
import { SettingsIcon } from '@ui/icons/material/Settings';
import { PersonIcon } from '@ui/icons/material/Person';
import { MovieIcon } from '@ui/icons/material/Movie';
import { WarningIcon } from '@ui/icons/material/Warning';
import { CameraAltIcon } from '@ui/icons/material/CameraAlt';
import { toast } from '@ui/toast/toast';
import { useSettings } from '@ui/settings/use-settings';
import { DialogTrigger } from '@ui/overlays/dialog/dialog-trigger';
import { ChangeAvatarDialog } from './change-avatar-dialog';


// Cores para perfis novos
const COLORS = ['bg-red-600', 'bg-blue-600', 'bg-[#0065FF]', 'bg-amber-600', 'bg-purple-600', 'bg-emerald-600'];

export function ProfileGate() {
  const { user } = useAuth();
  const { activeProfile, selectProfile, isSwitchingProfile } = useActiveProfile();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { branding } = useSettings();

  // UI States
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);
  const [isManageMode, setIsManageMode] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [avatarProfileId, setAvatarProfileId] = useState<number | null>(null);

  // RESET TOTAL
  useEffect(() => {
    setSelectedProfileId(null);
    setEditingId(null);
    setIsManageMode(false);
    setDeletingId(null);
  }, [location]);

  const t = {
    title: 'Quem está assistindo?',
    addProfile: 'Adicionar Perfil',
    createTitle: 'Novo Perfil',
    namePlaceholder: 'Nome',
    cancel: 'Cancelar',
    save: 'Criar',
    manageProfiles: 'Gerenciar Perfis',
    done: 'Concluído',
    deleteTitle: 'Excluir Perfil?',
    deleteConfirm: 'Tem certeza? Todos os dados deste perfil serão perdidos.',
    deleteBtn: 'Excluir',
    lastProfileError: 'Você deve manter pelo menos um perfil.'
  };

  const { data: profiles = [], isLoading } = useQuery({
    queryKey: ['user', 'profiles'],
    queryFn: () => apiClient.get('user/profiles').then(r => r.data),
    refetchOnWindowFocus: true,
    enabled: !!user,
  });

  // --- MUTAÇÕES ---
  const createProfile = useMutation({
    mutationFn: (name: string) => apiClient.post('user/profiles', {
      name,
      color: COLORS[Math.floor(Math.random() * COLORS.length)]
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'profiles'] });
      setIsCreating(false);
      setNewName('');
      toast('Perfil criado com sucesso!');
    },
    onError: () => toast.danger('Erro ao criar perfil'),
  });

  const updateProfile = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      apiClient.put(`user/profiles/${id}`, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'profiles'] });
      setEditingId(null);
      toast('Perfil atualizado!');
    },
    onError: () => toast.danger('Erro ao atualizar perfil'),
  });

  const deleteProfileMutation = useMutation({
    mutationFn: (id: number) => apiClient.delete(`user/profiles/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'profiles'] });
      setDeletingId(null);
      toast('Perfil excluído');
    },
    onError: () => toast.danger('Erro ao excluir perfil'),
  });

  if (activeProfile || !user) {
    return <Outlet />;
  }

  // --- HANDLERS ---
  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (newName.trim()) createProfile.mutate(newName);
  };

  const handleProfileClick = (profile: any) => {
    // Se estiver editando ou gerenciando, bloqueia a entrada
    if (isManageMode || editingId) return;

    setSelectedProfileId(profile.id);
    setTimeout(() => selectProfile(profile), 800);
  };

  const startEditing = (e: React.MouseEvent, profile: any) => {
    e.stopPropagation();
    e.preventDefault();
    setEditingId(profile.id);
    setEditName(profile.name);
  };

  const saveEditing = (e: React.MouseEvent, profile: any) => {
    e.stopPropagation();
    e.preventDefault();
    if (editName.trim()) {
      updateProfile.mutate({ id: profile.id, name: editName });
    } else {
      toast.danger('O nome não pode estar vazio!');
    }
  };

  const cancelEditing = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setEditingId(null);
  };

  const requestDelete = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    e.preventDefault();
    if (profiles.length <= 1) {
      toast.danger(t.lastProfileError);
      return;
    }
    setDeletingId(id);
  };

  const confirmDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (deletingId !== null) {
      deleteProfileMutation.mutate(deletingId);
    }
  };

  // ESTILOS DE FORÇA BRUTA PARA ÍCONES
  const iconStyle = { width: '40px', height: '40px', pointerEvents: 'none' as const };
  const smallIconStyle = { width: '24px', height: '24px' };

  return (
    <div className="min-h-screen bg-[#000005] flex flex-col items-center justify-center p-6 animate-in fade-in duration-700 relative">

      <div className="absolute top-8 md:top-12 flex flex-col items-center gap-2">
        {branding?.logo_light ? (
          <img
            src={branding.logo_light}
            alt="Logo"
            className="max-h-[32px] md:max-h-[42px] w-auto object-contain"
            loading="eager"
            fetchPriority="high"
            decoding="sync"
            draggable={false}
          />
        ) : (
          <span className="text-xl font-bold tracking-[0.2em] text-zinc-200">IMPIDIUM</span>
        )}
      </div>

      <div className="flex-1 flex flex-col items-center justify-center w-full max-w-7xl">
        <h1 className="text-3xl md:text-6xl font-light text-zinc-100 mb-16 md:mb-[99px] text-center tracking-tight">
          {t.title}
        </h1>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div className="flex flex-wrap justify-center items-start gap-[30px] md:gap-[60px]">
              {profiles.map((profile: any) => (
                <div key={profile.id} className="group relative flex flex-col items-center gap-4 w-[140px] md:w-[200px]">

                  <div className="relative">
                    {/* BOTÃO DO AVATAR - Z-INDEX 0 */}
                    <button
                      type="button"
                      onClick={() => handleProfileClick(profile)}
                      className={`
                        relative w-[130px] h-[130px] md:w-[200px] md:h-[200px] 
                        rounded-[4px] overflow-hidden transition-all duration-300 focus:outline-none z-0
                        ${isManageMode ? 'cursor-default brightness-50' : 'hover:scale-105 shadow-2xl'}
                        ${selectedProfileId === profile.id ? 'ring-4 ring-white animate-profilePulse' : ''}
                      `}
                      style={{ borderRadius: '4px' }}
                    >
                      <div className={`absolute inset-0 ${profile.color || 'bg-blue-600'} flex items-center justify-center`}>
                        {profile.avatar ? (
                          <img src={profile.avatar} alt={profile.name} className="w-full h-full object-cover" />
                        ) : (
                          <PersonIcon className="text-white opacity-80" style={{ width: '100px', height: '100px' }} />
                        )}
                      </div>
                    </button>

                    {/* LOADING PREMIUM - CHASING LIGHT WITH GLOW */}
                    {selectedProfileId === profile.id && (
                      <div className="absolute inset-[-8px] z-20 pointer-events-none">
                        <svg
                          className="w-full h-full drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]"
                          viewBox="0 0 100 100"
                          preserveAspectRatio="none"
                        >
                          <rect
                            x="1"
                            y="1"
                            width="98"
                            height="98"
                            rx="4"
                            fill="none"
                            stroke="white"
                            strokeWidth="2"
                            strokeDasharray="40 160"
                            className="animate-borderDash"
                          />
                        </svg>
                      </div>
                    )}

                    {/* CAMADA DE ÍCONES DE AÇÃO - Z-INDEX 50 */}
                    {isManageMode && !editingId && !selectedProfileId && (
                      <div className="absolute inset-0 z-50 pointer-events-none flex items-center justify-center">
                        <div className="absolute top-0 right-0 pointer-events-auto transform translate-x-2 -translate-y-2">
                          <button
                            type="button"
                            onClick={(e) => startEditing(e, profile)}
                            className="bg-zinc-900 text-zinc-200 hover:text-white hover:bg-black p-3 rounded-full border-2 border-zinc-500 shadow-xl transition-all hover:scale-110 cursor-pointer"
                          >
                            <EditIcon style={{ width: '32px', height: '32px' }} />
                          </button>
                        </div>
                        <div className="absolute top-0 left-0 pointer-events-auto transform -translate-x-2 -translate-y-2">
                          <button
                            type="button"
                            onClick={(e) => requestDelete(e, profile.id)}
                            className="bg-zinc-900 text-zinc-400 hover:text-red-500 hover:bg-black p-3 rounded-full border-2 border-zinc-500 shadow-xl transition-all hover:scale-110 cursor-pointer"
                          >
                            <DeleteIcon style={{ width: '32px', height: '32px' }} />
                          </button>
                        </div>
                        <div className="pointer-events-auto">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAvatarProfileId(profile.id);
                            }}
                            className="bg-black/70 text-white hover:bg-black/90 p-4 rounded-full border-2 border-white shadow-xl transition-all hover:scale-110 cursor-pointer"
                          >
                            <CameraAltIcon style={{ width: '40px', height: '40px' }} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* CAMPO DE EDIÇÃO */}
                  <div className="text-center w-full z-50">
                    {editingId === profile.id ? (
                      <div className="flex flex-col items-center gap-3 mt-2">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="w-full bg-zinc-900 border-2 border-zinc-500 rounded px-3 py-2 text-xl text-center text-white focus:border-[#0065FF] outline-none shadow-lg"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEditing(e as any, profile);
                          }}
                        />
                        <div className="flex gap-4 pointer-events-auto">
                          <button
                            type="button"
                            onClick={(e) => saveEditing(e, profile)}
                            className="text-white bg-green-600 p-3 rounded-full hover:bg-green-500 shadow-lg border border-zinc-700 cursor-pointer"
                          >
                            <CheckIcon style={{ width: '32px', height: '32px' }} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => cancelEditing(e)}
                            className="text-white bg-red-600 p-3 rounded-full hover:bg-red-500 shadow-lg border border-zinc-700 cursor-pointer"
                          >
                            <CloseIcon style={{ width: '32px', height: '32px' }} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <span className={`block text-xl md:text-3xl font-normal text-center mt-2 ${selectedProfileId === profile.id ? 'text-white' : 'text-zinc-500 group-hover:text-white'}`}>
                        {profile.name}
                      </span>
                    )}
                  </div>
                </div>
              ))
              }

              {/* Botão ADICIONAR (+) */}
              {
                !isCreating && profiles.length < 4 && !isManageMode && (
                  <div className="group flex flex-col items-center gap-4 w-[140px] md:w-[200px]">
                    <button
                      onClick={() => setIsCreating(true)}
                      className="w-[130px] h-[130px] md:w-[200px] md:h-[200px] rounded-[4px] border-[3px] border-zinc-700 flex items-center justify-center bg-transparent group-hover:bg-zinc-800 group-hover:border-zinc-500 transition-all"
                    >
                      <AddIcon className="text-zinc-500 group-hover:text-zinc-200" style={{ width: '80px', height: '80px' }} />
                    </button>
                    <span className="text-xl md:text-3xl font-normal text-zinc-500 group-hover:text-zinc-300 transition-colors mt-2 text-center">
                      {t.addProfile}
                    </span>
                  </div>
                )
              }
            </div >

            <div className="mt-24 flex justify-center">
              <button
                onClick={() => setIsManageMode(!isManageMode)}
                className={`
                    flex items-center gap-3 p-[10px] rounded-[4px] text-sm font-bold uppercase tracking-widest transition-all active:scale-95
                    ${isManageMode ? 'bg-white text-black hover:bg-zinc-200' : 'bg-transparent border border-zinc-500 text-zinc-400 hover:text-white hover:border-white'}
                `}
                style={{
                  padding: '10px 32px',
                  backgroundColor: isManageMode ? '#ffffff' : 'transparent',
                  color: isManageMode ? '#000000' : undefined,
                  borderColor: isManageMode ? 'transparent' : undefined,
                  borderRadius: '4px'
                }}
              >
                {isManageMode ? <CheckIcon className="text-black" style={smallIconStyle} /> : <SettingsIcon style={smallIconStyle} />}
                {isManageMode ? t.done : t.manageProfiles}
              </button>
            </div>
          </>
        )}
      </div >

      {/* MODAL: CRIAR PERFIL */}
      {
        isCreating && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 z-[9999] animate-in fade-in zoom-in-95 duration-300">
            <div className="bg-[#000005] border border-zinc-700 p-8 rounded-2xl w-full max-w-md shadow-2xl relative z-[10000]">
              <h3 className="text-2xl font-light text-white mb-8 text-center">{t.createTitle}</h3>
              <form onSubmit={handleCreate} className="space-y-6">
                <input
                  autoFocus
                  type="text"
                  placeholder={t.namePlaceholder}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-600 px-4 py-4 text-white placeholder:text-zinc-500 focus:outline-none focus:border-white transition-colors text-lg"
                  maxLength={20}
                  style={{ borderRadius: '4px' }}
                />
                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsCreating(false)}
                    className="flex-1 py-3 border border-zinc-600 text-zinc-400 hover:text-white hover:border-white transition-all uppercase tracking-wider text-sm font-bold"
                    style={{
                      background: 'transparent',
                      border: '1px solid #52525b',
                      color: '#a1a1aa',
                      padding: '12px',
                      borderRadius: '4px'
                    }}
                  >
                    {t.cancel}
                  </button>
                  <button
                    type="submit"
                    disabled={!newName.trim() || createProfile.isPending}
                    className="flex-1 py-3 bg-white text-black hover:bg-zinc-200 disabled:opacity-50 transition-all uppercase tracking-wider text-sm font-bold"
                    style={{
                      backgroundColor: (!newName.trim() || createProfile.isPending) ? '#52525b' : '#ffffff',
                      color: (!newName.trim() || createProfile.isPending) ? '#a1a1aa' : '#000000',
                      padding: '12px',
                      cursor: (!newName.trim() || createProfile.isPending) ? 'not-allowed' : 'pointer',
                      borderRadius: '4px'
                    }}
                  >
                    {t.save}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* MODAL: EXCLUIR PERFIL */}
      {
        deletingId !== null && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 z-[9999] animate-in fade-in zoom-in-95 duration-300">
            <div className="bg-[#000005] border border-zinc-700 p-8 rounded-2xl w-full max-w-md shadow-2xl relative z-[10000]">
              <div className="flex flex-col items-center gap-4 text-center mb-6">
                <WarningIcon className="text-red-600" style={{ width: '48px', height: '48px' }} />
                <h3 className="text-2xl font-bold text-white">{t.deleteTitle}</h3>
              </div>
              <p className="text-zinc-400 mb-8 text-center text-lg">{t.deleteConfirm}</p>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setDeletingId(null)}
                  className="flex-1 py-3 border border-zinc-600 text-zinc-400 hover:text-white hover:border-white transition-all uppercase tracking-wider text-sm font-bold"
                  style={{
                    background: 'transparent',
                    border: '1px solid #52525b',
                    color: '#a1a1aa',
                    padding: '12px',
                    borderRadius: '4px'
                  }}
                >
                  {t.cancel}
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  disabled={deleteProfileMutation.isPending}
                  className="flex-1 py-3 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-all uppercase tracking-wider text-sm font-bold cursor-pointer"
                  style={{
                    backgroundColor: deleteProfileMutation.isPending ? '#52525b' : '#dc2626',
                    color: '#ffffff',
                    padding: '12px',
                    borderRadius: '4px',
                    cursor: deleteProfileMutation.isPending ? 'not-allowed' : 'pointer'
                  }}
                >
                  {deleteProfileMutation.isPending ? 'Excluindo...' : t.deleteBtn}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* FULL-SCREEN LOADING OVERLAY during profile switch */}
      {
        isSwitchingProfile && (
          <div className="fixed inset-0 z-[99999] bg-[#000005] flex flex-col items-center justify-center animate-in fade-in duration-300">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-6" />
            <p className="text-zinc-400 text-lg animate-pulse">Carregando perfil...</p>
          </div>
        )
      }

      {/* MODAL: ALTERAR AVATAR */}
      {
        avatarProfileId !== null && (
          <ChangeAvatarDialog
            profileId={avatarProfileId}
            currentAvatarId={profiles.find((p: any) => p.id === avatarProfileId)?.avatar_id}
            onClose={() => setAvatarProfileId(null)}
          />
        )
      }
    </div >
  );
}